<template>
  <EdgeStack :id="esId" :busy="busy">
    <template v-slot:header>
      <div class="pb-3 panel-title">{{ getTitleText }}</div>
      <XStepper
        :id="esId"
        :steps="stepperSteps"
        :mode="stepperMode"
        v-model="stepperCurrentStep"
        ref="xstepper"
      />
    </template>

    <template v-slot:footer>
      <XStepperNavigation
        v-if="isEditing"
        :id="esId"
        :steps="stepperSteps"
        :submit-button-text="getSubmitButtonText"
        @submit="onSubmit"
        v-model="stepperCurrentStep"
      />

      <AppButton
        v-if="!isEditing"
        :text="getSubmitButtonText"
        @click="onSubmit({ step: stepperCurrentStep })"
      />
    </template>

    <template #default>
      <keep-alive>
        <template v-if="stepperCurrentStep === 1">
          <RoleAddEditStep1
            ref="step1"
            :primaryKey="primaryKeyProxy"
            :formData="getStep1FormData"
            :availableScreenActionsMap="availableScreenActionsMap"
            :availablePermissions="screenPermissions"
            :isEditing="isEditing"
            @save="onSave($event)"
            @dirty="onDirty(true)"
          />
        </template>

        <template v-else-if="stepperCurrentStep === 2">
          <RoleAddEditStep2
            ref="step2"
            :primary-key="primaryKeyProxy"
            :form-data="getStep2FormData"
            :availableHardwareActionsMap="availableHardwareActionsMap"
            :availablePermissions="hardwarePermissions"
            :is-editing="isEditing"
            @save="onSave($event)"
            @dirty="onDirty(true)"
          />
        </template>
      </keep-alive>
    </template>
  </EdgeStack>
</template>

<script>
import { AppButton } from '@/components/form'
import { EdgeStack } from '@/components/modals'
import { XStepper, XStepperNavigation } from '@/components/stepper'
import permissionApi from '@/config/api/permission'
import { hardwareActions } from './helper'
import RoleAddEditStep1 from './RoleAddEditStep1'
import RoleAddEditStep2 from './RoleAddEditStep2'

export default {
  name: 'RoleAddEdit',

  components: {
    EdgeStack,
    XStepper,
    XStepperNavigation,
    AppButton,
    RoleAddEditStep1,
    RoleAddEditStep2,
  },

  props: {
    esId: {
      type: String,
      default: 'role-add-edit',
    },
    stepperMode: {
      type: String,
      default: 'free',
    },
    stepperStep: {
      type: Number,
      default: 1,
    },

    primaryKey: {
      required: false,
    },

    rawData: {
      type: Object,
      required: false,
    },

    busy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      stepperSteps: [
        {
          title: 'Screen',
          free: true,
        },
        {
          title: 'Hardware Action',
          free: false,
        },
      ],

      stepperCurrentStep: 1,
      rawDataProxy: null,
      primaryKeyProxy: null,
      availableScreenActionsMap: {},
      availableHardwareActionsMap: {},
      screenPermissions: [],
      hardwarePermissions: [],
    }
  },
  async created() {
    this.getPermissions()
  },
  computed: {
    isEditing() {
      return !!this.rawDataProxy
    },

    getTitleText() {
      return this.isEditing
        ? 'Edit Roles and Permission'
        : 'Add Roles and Permission'
    },

    getSubmitButtonText() {
      if (this.stepperCurrentStep > this.stepperSteps.length - 1)
        return this.$t('components.stepNavigation.finish')

      return this.isEditing
        ? this.$t('components.stepNavigation.update')
        : this.$t('components.stepNavigation.saveAndContinue')
    },

    getStep1FormData() {
      if (this.rawDataProxy) {
        return {
          role_name: this.rawDataProxy.role_name,
          screenPermissions: this.rawDataProxy.permissions
            ? this.rawDataProxy.permissions.filter(
                (item) => !hardwareActions.includes(item.name)
              )
            : [],
          hardwarePermissions: this.rawDataProxy.permissions
            ? this.rawDataProxy.permissions.filter((item) =>
                hardwareActions.includes(item.name)
              )
            : [],
        }
      }
      return null
    },
    getStep2FormData() {
      if (this.rawDataProxy) {
        return {
          screenPermissions: this.rawDataProxy.permissions
            ? this.rawDataProxy.permissions.filter(
                (item) => !hardwareActions.includes(item.name)
              )
            : [],
          hardwarePermissions: this.rawDataProxy.permissions
            ? this.rawDataProxy.permissions.filter((item) =>
                hardwareActions.includes(item.name)
              )
            : [],
        }
      }
      return null
    },
  },

  mounted() {
    // reset current step to 1 on closed
    this.$edgeStack.emitter.on(
      this.$edgeStack.getEventName('closed', this.esId),
      () => {
        this.resetInitialData()
      }
    )
  },

  beforeDestroy() {
    this.$edgeStack.emitter.off(
      this.$edgeStack.getEventName('closed', this.esId)
    )
  },

  watch: {
    rawData: {
      deep: false,
      immediate: true,
      handler(data) {
        if (data) {
          this.rawDataProxy = data
          this.primaryKeyProxy = data.id
        }
      },
    },

    stepperStep: {
      deep: false,
      immediate: true,
      handler(updatedStep) {
        this.stepperCurrentStep = updatedStep
      },
    },
  },

  methods: {
    getPermissions: async function() {
      try {
        // populate available actions
        let response = await this.$http.get(permissionApi.availableActions)
        let data = response.data.data
        data.forEach((item) => {
          if (hardwareActions.includes(item.permission_name)) {
            this.availableHardwareActionsMap[item.permission_name] =
              item.available_actions
          } else {
            this.availableScreenActionsMap[item.permission_name] =
              item.available_actions
          }
        })

        response = await this.$http.get(permissionApi.index)
        data = response.data.data

        const screenResult = []
        const hardwareResult = []
        data.forEach((item) => {
          const obj = {
            id: item.id,
            action_types: [],
            name: item.name,
          }
          if (hardwareActions.includes(item.name)) {
            hardwareResult.push(obj)
          } else {
            screenResult.push(obj)
          }
        })
        // this.permissions = result
        this.screenPermissions = screenResult
        this.hardwarePermissions = hardwareResult
      } catch (error) {
        // todo: remove logs
        console.log(error)
        throw new Error(error.status)
      }
    },
    onDirty(type, id = this.esId) {
      return type === true
        ? this.$edgeStack.shouldConfirm(id)
        : this.$edgeStack.shouldNotConfirm(id)
    },

    async onSave(e = { step: null, data: {} }) {
      this.rawDataProxy = e.data
      if (e.step === 1) {
        this.primaryKeyProxy = e.data.id

        // check uniqueness & push to unassignedLocks array

        // free all steps
        const steps = [
          {
            title: 'Screen',
            free: true,
          },
          {
            title: 'Hardware Action',
            free: true,
          },
        ]

        this.$xStepper.defineSteps(this.esId, steps)
        this.beforeNextStep({ to: 2 })
      }

      if (e.step === 2) {
        // free only first step -> as we'r about to reset & close -> set it to initial state
        this.$xStepper.defineSteps(this.esId, this.stepperSteps)
        this.onLastStep()

        // reload unassigned fleets to sync with the server
        await this.fetchData({ onlyUnassignedLocks: true })
      }
    },

    beforeNextStep({ to = 2 }) {
      // define what happens about confirmation before exiting on next step (2)
      // on edit mode->after updating->should not confirm unless event triggered by user
      // on add mode->after saving->should confirm
      if (this.stepperMode === 'free') {
        this.$edgeStack.shouldNotConfirm(this.esId)
      } else {
        this.$edgeStack.shouldConfirm(this.esId)
      }

      if (to) {
        this.$xStepper.navigate(this.esId).to(to)
      } else {
        this.$xStepper.navigate(this.esId).next()
      }
    },

    onLastStep() {
      // notify that the table view needs to be updated
      this.$emit('refresh')

      // Should not confirm as we'r about to close it
      this.$edgeStack.shouldNotConfirm(this.esId)
      this.$edgeStack.close(this.esId)

      this.$edgeStack.emitter.on(
        this.$edgeStack.getEventName('closed', this.esId),
        () => {
          this.resetInitialData()
        }
      )
    },
    resetInitialData() {
      this.stepperCurrentStep = 1
      this.primaryKeyProxy = null
      this.rawDataProxy = null
    },

    onSubmit(step) {
      this.$refs[`step${step.step}`].submit()
    },
  },
}
</script>

<style scoped>
.panel-title {
  font-size: 22px;
  font-weight: 500;
  color: #2e2e39;
  margin-top: -30px;
  margin-bottom: 6px;
}
</style>
